/**
 * Browser-side cache of finished analyzer reports, so report URLs survive navigation and reloads.
 * Memory first (always works), IndexedDB behind it (survives reloads, shared across browser tabs).
 * Temporary by design: only the most recent MAX_REPORTS are kept, each for at most MAX_AGE_MS.
 */
import { TbcSessionSnapshot } from './types';

export interface CachedReport {
    id: string;
    basePath: string;
    savedAt: number;
    session: TbcSessionSnapshot;
    extra?: unknown;
    result: unknown;
    /** REPORT_CACHE_VERSION at save time; absent on entries saved before versioning. */
    version?: number;
}

/**
 * Bump whenever an analyzer's result shape or calculations change (new fields, scoring, costs,
 * boss lists). Older entries then count as misses: Consumes rebuilds them, other tabs ask for a re-run.
 * 2: consume score modes.
 * 3: relaxed guardian weight.
 * 4: score part coverage.
 * 5: optimal consume cost.
 * 6: hunter adamantite sharpening stones, 50-charge drums, boss encounter order, boss columns for one raid.
 * 7: stat values credit +heal per observed HoT tick (Lifebloom weighted by stacks) and per bloom.
 * 8: stat values scale direct +heal by observed crit rate; Holy Shock heals counted.
 */
const REPORT_CACHE_VERSION = 9;
const DB_NAME = 'wow_calcs.reports';
const STORE = 'reports';
const MAX_REPORTS = 20;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const memory = new Map<string, CachedReport>();
let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
    if (!dbPromise) {
        dbPromise = new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, 1);
            request.onupgradeneeded = () => {
                const store = request.result.createObjectStore(STORE, { keyPath: 'id' });
                store.createIndex('savedAt', 'savedAt');
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
        dbPromise.catch(() => {
            dbPromise = null;
        });
    }
    return dbPromise;
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

function isFresh(entry: CachedReport): boolean {
    return entry.version === REPORT_CACHE_VERSION && Date.now() - entry.savedAt <= MAX_AGE_MS;
}

export function newReportId(): string {
    return Math.random().toString(36).slice(2, 10);
}

async function pruneDb(db: IDBDatabase): Promise<void> {
    const store = db.transaction(STORE, 'readwrite').objectStore(STORE);
    let excess = (await requestToPromise(store.count())) - MAX_REPORTS;
    const cutoff = Date.now() - MAX_AGE_MS;
    const cursorRequest = store.index('savedAt').openCursor();
    await new Promise<void>((resolve, reject) => {
        cursorRequest.onsuccess = () => {
            const cursor = cursorRequest.result;
            if (!cursor) {
                resolve();
                return;
            }
            const entry = cursor.value as CachedReport;
            if (excess <= 0 && entry.savedAt >= cutoff) {
                resolve();
                return;
            }
            cursor.delete();
            excess -= 1;
            cursor.continue();
        };
        cursorRequest.onerror = () => reject(cursorRequest.error);
    });
}

export async function saveReport(report: Omit<CachedReport, 'version'>): Promise<void> {
    const entry: CachedReport = { ...report, version: REPORT_CACHE_VERSION };
    memory.set(entry.id, entry);
    try {
        const db = await openDb();
        await requestToPromise(db.transaction(STORE, 'readwrite').objectStore(STORE).put(entry));
        await pruneDb(db);
    } catch (err) {
        console.warn('Report cache: could not persist report', err);
    }
}

export async function loadReport(id: string): Promise<CachedReport | null> {
    const cached = memory.get(id);
    if (cached) {
        return isFresh(cached) ? cached : null;
    }
    try {
        const db = await openDb();
        const entry = (await requestToPromise(db.transaction(STORE).objectStore(STORE).get(id))) as
            | CachedReport
            | undefined;
        if (!entry || !isFresh(entry)) {
            return null;
        }
        memory.set(id, entry);
        return entry;
    } catch (err) {
        console.warn('Report cache: could not read report', err);
        return null;
    }
}
